import { AtUri, ensureValidDid, ensureValidHandle } from "@atproto/syntax";
import { CID } from "multiformats/cid";

export function postUri(input: string, requireDid = false): AtUri {
  let value = input.trim();
  if (requireDid && (!input.startsWith("at://") || input !== value)) {
    throw new Error("Record references must be exact DID-based AT URIs.");
  }
  if (!value.startsWith("at://")) {
    const url = new URL(value);
    const match = /^\/profile\/([^/]+)\/post\/([^/]+)\/?$/.exec(url.pathname);
    if (url.protocol !== "https:" || !match) {
      throw new Error("Enter an AT URI or a bsky.app post link.");
    }
    value = `at://${decodeURIComponent(match[1])}/app.bsky.feed.post/${
      decodeURIComponent(match[2])
    }`;
  }
  if (!/^at:\/\/[^/]+\/app\.bsky\.feed\.post\/[^/?#]+$/.test(value)) {
    throw new Error("Expected an app.bsky.feed.post record URI.");
  }
  const uri = new AtUri(value);
  if (
    uri.collection !== "app.bsky.feed.post" ||
    !/^[a-zA-Z0-9._~:-]{1,512}$/.test(uri.rkey) ||
    uri.rkey === "." || uri.rkey === ".." || uri.hash || uri.search ||
    uri.toString() !== value
  ) {
    throw new Error("Expected an app.bsky.feed.post record URI.");
  }
  if (uri.host.startsWith("did:")) ensureValidDid(uri.host);
  else {
    if (requireDid) throw new Error("Record references must use a DID.");
    ensureValidHandle(uri.host);
  }
  return uri;
}

export function didDocumentUrl(did: string): URL {
  ensureValidDid(did);
  if (/^did:plc:[a-z2-7]{24}$/.test(did)) {
    return new URL(`https://plc.cerulea.blue/${did}`);
  }
  if (did.startsWith("did:web:")) {
    const parts = did.slice(8).split(":").map(decodeURIComponent);
    const host = parts.shift()!;
    if (
      parts.some((part) =>
        !part || part === "." || part === ".." || /[/?#\\]/.test(part)
      )
    ) {
      throw new Error("Invalid did:web path.");
    }
    const url = new URL(`https://${host}/`);
    if (url.host !== host || url.username || url.password) {
      throw new Error("Invalid did:web host.");
    }
    url.pathname = parts.length
      ? `/${parts.map(encodeURIComponent).join("/")}/did.json`
      : "/.well-known/did.json";
    return url;
  }
  throw new Error("Only did:plc and did:web identities are supported.");
}

export function webUrl(value: string): string | undefined {
  try {
    const url = new URL(value);
    if (
      (url.protocol === "https:" || url.protocol === "http:") &&
      !url.username && !url.password
    ) return url.href;
  } catch { /* Untrusted record links can be malformed. */ }
}

export function blobUrl(
  pds: string | undefined,
  did: string,
  cid: string,
): string | undefined {
  if (!pds) return;
  try {
    CID.parse(cid);
    const url = new URL(
      `${pds.replace(/\/$/, "")}/xrpc/com.atproto.sync.getBlob`,
    );
    if (url.protocol !== "https:") return;
    url.search = new URLSearchParams({ did, cid }).toString();
    return url.href;
  } catch { /* Unsupported blob references stay unrendered. */ }
}

export function bskyUrl(uri: string): string {
  const parsed = postUri(uri, true);
  return `https://bsky.app/profile/${parsed.host}/post/${parsed.rkey}`;
}
